import { Archive, ArchiveRestore, Check, CheckCircle2, Copy, Crosshair, Film, History, Plus, Send, Trash2 } from "lucide-react";
import { useEffect, useState, type CSSProperties } from "react";
import { addBacklog, bulkArchiveBacklog, deleteBacklog, getScan, isScannedScene, listBacklog, listChannelsApi, listMusicTracks, setBacklogArchived, subscribeScan, type BacklogDraft, type BacklogEntry, type MusicTrack, type ScanResult } from "../api";
import { PageHeader } from "../components/PageHeader";
import { SceneRow } from "../components/SceneRow";
import { cardStyle, ctaSmallStyle, fieldLabelStyle, inputStyle, monoInputStyle } from "../components/styles";
import { useIsMobile } from "../lib/useIsMobile";
import { validateTimestamp, validateYouTubeUrl } from "../lib/validate";

interface Props {
  /** Pre-fill the New Job form with this entry's fields. */
  onUseInNewJob: (draft: BacklogDraft) => void;
  /** Called with the new job id after a candidate renders — routes to the Queue. */
  onNavigateQueue?: (jobId: string) => void;
  /** Scan id of an in-flight auto-scan — shows a live progress card until it settles. */
  activeScanId?: string | null;
  /** Called when the active scan reaches done/failed so the parent can clear it. */
  onScanSettled?: () => void;
  /** Called when the operator hits "Edit & retry" on a failed locate — re-opens New Job pre-filled. */
  onEditScan?: (scan: ScanResult) => void;
  /** Routes to the Requests view ("Scan history") — the durable scan/locate log. */
  onNavigateRequests?: () => void;
  /** Server CHANNELS_ENABLED flag (threaded from App). Gates the inline
   *  scanned-scenes channel chip + cross-channel hint; false ⇒ single-channel. */
  channelsEnabled?: boolean;
}

/** Which slice of the scanned scenes is on screen. Single-select; every scene
 *  lives under exactly ONE chip (they partition the list), so nothing can
 *  hide (ADR-049 never-silently-vanish):
 *    fresh    = !renderedJobId && !archived   (the working set)
 *    rendered = renderedJobId set             (archived-or-not — a rendered scene shows here)
 *    archived = archived && !renderedJobId    (shelved, restorable)
 */
type SceneFilter = "fresh" | "rendered" | "archived";

/** sessionStorage key for the active chip — a reload mid-session keeps
 *  context, a NEW session starts back on Fresh. */
const SCENE_FILTER_STORAGE_KEY = "ql-backlog-scene-filter";

function sceneFilterOf(e: BacklogEntry): SceneFilter {
  if (e.renderedJobId) return "rendered";
  if (e.archived) return "archived";
  return "fresh";
}

const EMPTY_DRAFT: BacklogDraft = {
  label: "",
  url: "",
  from: "",
  to: "",
  startPhrase: "",
  endPhrase: "",
  host: "",
  guests: [],
  topic: "",
  notes: "",
};

/**
 * "Backlog" — pre-render staging table. The operator collects YouTube
 * candidates here over the course of a session (URLs, approximate
 * timestamps, notes) and promotes one to a real render with a single
 * click. Server-stored so the list survives browser cache clears and is
 * reachable from any device pointed at the same instance.
 */
export function Backlog({ onUseInNewJob, onNavigateQueue, activeScanId, onScanSettled, onEditScan, onNavigateRequests, channelsEnabled }: Props): React.JSX.Element {
  const isMobile = useIsMobile();
  const [items, setItems] = useState<BacklogEntry[]>([]);
  const [loaded, setLoaded] = useState(false);
  // Background-music catalog for the per-scene picker. Fetched once; null while
  // loading or if it failed (the picker then offers only "Auto"). Best-effort —
  // a missing catalog never blocks rendering (the pipeline falls back to Auto).
  const [musicTracks, setMusicTracks] = useState<MusicTrack[] | null>(null);
  useEffect(() => {
    let alive = true;
    void listMusicTracks()
      .then((t) => { if (alive) setMusicTracks(t); })
      .catch(() => { if (alive) setMusicTracks(null); });
    return () => { alive = false; };
  }, []);
  const [draft, setDraft] = useState<BacklogDraft>(EMPTY_DRAFT);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Inline scanned-scenes list owns the post-render confirm toast (one at a
  // time) and the per-row inline-delete confirm.
  const [toast, setToast] = useState<{ jobId: string } | null>(null);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  // Active scanned-scenes filter chip. Session-persisted: survives a reload
  // mid-session, resets to Fresh in a new session (sessionStorage semantics).
  const [sceneFilter, setSceneFilterState] = useState<SceneFilter>(() => {
    try {
      const saved = window.sessionStorage.getItem(SCENE_FILTER_STORAGE_KEY);
      return saved === "rendered" || saved === "archived" ? saved : "fresh";
    } catch {
      return "fresh";
    }
  });
  const setSceneFilter = (f: SceneFilter): void => {
    setSceneFilterState(f);
    // A chip switch invalidates any open "Archive N scenes?" confirm — the
    // visible set (and therefore N) changes under it.
    setConfirmingArchiveGroupKey(null);
    try {
      window.sessionStorage.setItem(SCENE_FILTER_STORAGE_KEY, f);
    } catch {
      /* private mode / storage blocked — the chip still switches for this render */
    }
  };
  // Per-group inline confirm for "Archive remaining" (mirrors the per-row
  // delete confirm — one group at a time, keyed by the group's video key).
  const [confirmingArchiveGroupKey, setConfirmingArchiveGroupKey] = useState<string | null>(null);
  // Briefly glow the "Scanned scenes" section header when a finished scan drops
  // fresh candidates into it — so a successful scan visibly lands somewhere
  // instead of silently bumping a counter (the "my scan vanished" confusion).
  const [scenesJustGrew, setScenesJustGrew] = useState(false);
  // Cross-channel hint count (SCAN-12) — additive field from GET /api/backlog;
  // 0 unless CHANNELS_ENABLED and scenes exist under other channels.
  const [otherChannelScannedCount, setOtherChannelScannedCount] = useState(0);
  // Channel slug→displayName map for the modal's chip + hint. Empty unless
  // CHANNELS_ENABLED (the modal falls back to the raw slug when empty).
  const [channels, setChannels] = useState<{ slug: string; displayName: string }[]>([]);
  // Active channel slug — passed to the modal so the cross-channel hint never
  // names the channel the operator is already viewing (it lists OTHER channels).
  const [activeChannelSlug, setActiveChannelSlug] = useState<string>("");

  const clearFieldError = (key: string): void => {
    setFieldErrors((prev) => {
      if (!(key in prev)) return prev;
      const { [key]: _drop, ...rest } = prev;
      return rest;
    });
  };

  // Returns the fetched entries so callers (the locate-dedup highlight flow)
  // can inspect the fresh list without racing React state.
  const refresh = async (): Promise<BacklogEntry[]> => {
    try {
      const { items: fetched, otherChannelScannedCount: count } = await listBacklog();
      setItems(fetched);
      setOtherChannelScannedCount(count);
      return fetched;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return [];
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { void refresh(); }, []);

  // Channel display names for the Scene Library modal's chip + cross-channel
  // hint. Only meaningful when channels are on; on a single-channel instance
  // the endpoint is absent, so a failure is silently ignored (empty list →
  // the modal falls back to the raw slug, and the hint never shows anyway).
  useEffect(() => {
    if (!channelsEnabled) return;
    void listChannelsApi()
      .then((r) => {
        setChannels(r.channels.map((c) => ({ slug: c.slug, displayName: c.displayName })));
        setActiveChannelSlug(r.activeSlug);
      })
      .catch(() => { /* best-effort; modal degrades to slug text */ });
  }, [channelsEnabled]);

  // Live auto-scan progress. When the operator launches a scan from New Job, App
  // hands us its scanId; we subscribe to the SSE stream and show a progress card
  // until the scan settles, then refresh so the new candidates appear.
  //
  // Lifecycle: SSE is the fast path; a 20s poll of GET /api/scan/:id runs
  // alongside it for the scan's whole lifetime. There is deliberately NO
  // wall-clock cap — long podcasts routinely scan for 5–10+ minutes (a 4-hour
  // video takes ~10), and the old 240s "safety dismiss" here silently killed
  // the card on every one of them, which read as "scan is broken" while the
  // server finished fine minutes later. The poll also covers the two ways SSE
  // dies quietly: a dropped connection (poll keeps the card honest) and a
  // server restart mid-scan (scanStore is in-memory, so the record 404s after
  // a redeploy — three consecutive poll misses reports it as interrupted
  // instead of spinning forever).
  const [scan, setScan] = useState<ScanResult | null>(null);
  // Backlog entry to flash + scroll to when a locate deduped against a scene
  // that's already on the shelf — the answer to "I asked for it, where IS it?".
  const [highlightEntryId, setHighlightEntryId] = useState<string | null>(null);
  useEffect(() => {
    if (!activeScanId) return;
    setScan({ status: "queued", videoId: "" });
    let settled = false;
    let pollFails = 0;
    let last: ScanResult | null = null;

    const settle = (rec: ScanResult): void => {
      if (settled) return;
      settled = true;
      setScan(rec); // keep the record so the card shows the outcome (Found N / dup / error)
      if (rec.status === "done") {
        void refresh().then((fetched) => {
          if ((rec.candidateCount ?? 0) > 0) {
            setScenesJustGrew(true);
            window.setTimeout(() => setScenesJustGrew(false), 2200);
          }
          // Locate matched a scene that's already in the list: flash it and
          // bring it into view so "already in your Backlog" is self-evident.
          // The scene may live under a different filter chip (rendered, or
          // archived — a dedup never resurrects it) — switch to the chip that
          // contains it BEFORE scrolling, so the operator is never left
          // staring at nothing.
          if (rec.relatedEntryId) {
            // refresh() returns [] on a failed refetch — fall back to the
            // already-loaded state; if the target is nowhere, skip the
            // highlight rather than glowing a row invisible under the
            // current chip.
            const pool = fetched.length > 0 ? fetched : items;
            const target = pool.find((e) => e.id === rec.relatedEntryId);
            if (!target) return;
            setSceneFilter(sceneFilterOf(target));
            setHighlightEntryId(rec.relatedEntryId);
            window.setTimeout(() => {
              document.getElementById(`scene-${rec.relatedEntryId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 250);
            window.setTimeout(() => setHighlightEntryId(null), 6000);
          }
        });
      }
      onScanSettled?.();
      es.close();
      window.clearInterval(poll);
    };

    const handleRecord = (rec: ScanResult): void => {
      last = rec;
      if (rec.status === "done" || rec.status === "failed") settle(rec);
      else if (!settled) setScan(rec);
    };

    const es = subscribeScan(activeScanId, { update: handleRecord });

    const poll = window.setInterval(() => {
      if (settled) return;
      getScan(activeScanId)
        .then((rec) => { pollFails = 0; handleRecord(rec); })
        .catch(() => {
          pollFails++;
          // ~1 minute of consecutive misses: the record is gone (redeploy wiped
          // the in-memory scanStore) or the server is unreachable. Say so —
          // the durable Requests log ("Scan history") marks it "interrupted"
          // with a one-click retry after the restart.
          if (pollFails >= 3) {
            settle({
              ...(last ?? { videoId: "" }),
              status: "failed",
              errorMsg: "This scan was interrupted — most likely a server restart mid-scan. Retry it from Scan history, or run it again from New Job.",
            });
          }
        });
    }, 20_000);

    return () => { es.close(); window.clearInterval(poll); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScanId]);

  // Auto-dismiss the post-render toast after 2.5s; clear on unmount / re-toast.
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 2500);
    return () => window.clearTimeout(t);
  }, [toast]);

  const submitDraft = async (): Promise<void> => {
    if (adding) return;
    if (!draft.url?.trim() && !draft.label?.trim()) {
      setError("Need at least a URL or a label so the entry isn't blank.");
      return;
    }
    // Format-validate every populated field. Empty fields are fine —
    // the backlog is a staging area, not all info is known up front.
    const errs: Record<string, string> = {};
    if (draft.url?.trim()) {
      const urlErr = validateYouTubeUrl(draft.url);
      if (urlErr) errs.url = urlErr;
    }
    if (draft.from?.trim()) {
      const fromErr = validateTimestamp(draft.from, "Approx. start");
      if (fromErr) errs.from = fromErr;
    }
    if (draft.to?.trim()) {
      const toErr = validateTimestamp(draft.to, "End or duration");
      if (toErr) errs.to = toErr;
    }
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      setError(null);
      return;
    }
    setAdding(true);
    setError(null);
    try {
      // Coerce guests CSV string when typed by hand.
      const guestsRaw = (draft as { guests?: unknown }).guests;
      const guests = Array.isArray(guestsRaw)
        ? guestsRaw
        : typeof guestsRaw === "string"
        ? (guestsRaw as string).split(",").map((s) => s.trim()).filter(Boolean)
        : undefined;
      await addBacklog({ ...draft, guests });
      setDraft(EMPTY_DRAFT);
      setFieldErrors({});
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id: string): Promise<void> => {
    try {
      await deleteBacklog(id);
      setItems((cur) => cur.filter((e) => e.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  // A scanned scene finished its createJob PATCH: replace the row in place
  // (candidate:false + renderedJobId) and raise the queued-confirmation toast.
  const handleSceneRendered = (updated: BacklogEntry, jobId: string): void => {
    setItems((cur) => cur.map((e) => (e.id === updated.id ? updated : e)));
    setToast({ jobId });
  };

  // A non-render edit on a scene (the per-scene music pick) — keep the parent's
  // entries in sync so the choice survives a re-render of the Backlog.
  const handleSceneUpdated = (updated: BacklogEntry): void => {
    setItems((cur) => cur.map((e) => (e.id === updated.id ? updated : e)));
  };

  // Inline-confirm delete for a scanned scene (mirrors the manual-row delete but
  // clears the per-row confirm in `finally` so a failed DELETE doesn't freeze it).
  const handleSceneDelete = async (id: string): Promise<void> => {
    try {
      await deleteBacklog(id);
      setItems((cur) => cur.filter((e) => e.id !== id));
    } catch (err) {
      console.error("Failed to delete scanned scene", err);
    } finally {
      setConfirmingDeleteId(null);
    }
  };

  // Archive/restore ONE scene (SceneRow's Archive / Restore button). The row
  // moves between the Fresh and Archived chips — nothing is deleted.
  const handleToggleArchive = async (entry: BacklogEntry): Promise<void> => {
    try {
      const updated = await setBacklogArchived(entry.id, !entry.archived);
      setItems((cur) => cur.map((e) => (e.id === updated.id ? updated : e)));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  // Archive/restore a whole group in one request (per-group "Archive
  // remaining" / "Restore all"). Mirrors the server's stamping locally so the
  // rows move chips immediately without a full refetch.
  const handleBulkArchive = async (ids: string[], archived: boolean): Promise<void> => {
    try {
      await bulkArchiveBacklog(ids, archived);
      const now = Date.now();
      const idSet = new Set(ids);
      setItems((cur) =>
        cur.map((e) =>
          idSet.has(e.id)
            ? archived
              ? { ...e, archived: true, archivedAt: now }
              : { ...e, archived: false, archivedAt: undefined }
            : e,
        ),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setConfirmingArchiveGroupKey(null);
    }
  };

  // Scanned scenes (scan/locate origin, rendered + archived included):
  // always-visible inline list, newest-first. Derived, not state — recomputed
  // as items change.
  const sceneEntries = items.filter(isScannedScene).sort((a, b) => b.createdAt - a.createdAt);
  const freshCount = sceneEntries.filter((e) => sceneFilterOf(e) === "fresh").length;
  const renderedCount = sceneEntries.filter((e) => sceneFilterOf(e) === "rendered").length;
  const archivedCount = sceneEntries.filter((e) => sceneFilterOf(e) === "archived").length;

  // Group by source video (manual/urlless rows pool under one key), newest
  // group first by the newest scene inside it — a just-scanned video bubbles
  // to the top. Entries inside a group inherit the newest-first sort above.
  const sceneGroups: { key: string; entries: BacklogEntry[]; newestAt: number }[] = [];
  {
    const byKey = new Map<string, { key: string; entries: BacklogEntry[]; newestAt: number }>();
    for (const e of sceneEntries) {
      const key = e.videoId ?? e.url ?? "manual";
      let g = byKey.get(key);
      if (!g) {
        g = { key, entries: [], newestAt: 0 };
        byKey.set(key, g);
        sceneGroups.push(g);
      }
      g.entries.push(e);
      g.newestAt = Math.max(g.newestAt, e.createdAt);
    }
    sceneGroups.sort((a, b) => b.newestAt - a.newestAt);
  }
  // Only groups with at least one row under the active chip render — the
  // group-header counts + the other chips' counts keep the rest visible.
  const visibleGroups = sceneGroups
    .map((g) => ({ ...g, visible: g.entries.filter((e) => sceneFilterOf(e) === sceneFilter) }))
    .filter((g) => g.visible.length > 0);

  // The manual-entries list shows ONLY hand-added rows. Scan/locate candidates
  // render in the dedicated "Scanned scenes" section below.
  const inlineEntries = items
    .slice()
    .sort((a, b) => b.createdAt - a.createdAt)
    .filter((e) => e.origin !== "scan" && e.origin !== "locate");

  return (
    <div>
      <PageHeader
        eyebrow="Backlog"
        title="Clips to render later."
        subtitle="Save YouTube candidates with their approximate timestamps. Promote any row to a real render with one click — your typed fields pre-fill the New Job form."
      />

      {error && (
        <div
          style={{
            background: "rgba(248,113,113,.06)",
            border: "1px solid rgba(248,113,113,.30)",
            borderRadius: 12,
            padding: "10px 14px",
            color: "var(--ql-danger)",
            fontSize: 12,
            marginBottom: 14,
          }}
        >
          {error}
        </div>
      )}

      {/* Add-row form */}
      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 11,
            fontWeight: 700,
            color: "var(--ql-yellow)",
            textTransform: "uppercase",
            letterSpacing: ".08em",
            marginBottom: 14,
          }}
        >
          <Plus size={14} strokeWidth={2.5} /> Add to backlog
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
          <Field label="Label">
            <input
              value={draft.label ?? ""}
              onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
              placeholder='e.g. "Bessel — trauma rewires"'
              style={inputStyle}
            />
          </Field>
          <Field label="YouTube URL" error={fieldErrors.url}>
            <input
              value={draft.url ?? ""}
              onChange={(e) => { setDraft((d) => ({ ...d, url: e.target.value })); clearFieldError("url"); }}
              placeholder="https://www.youtube.com/watch?v=…"
              style={withError(monoInputStyle, !!fieldErrors.url)}
            />
          </Field>
          <Field label="Approx. start" error={fieldErrors.from}>
            <input
              value={draft.from ?? ""}
              onChange={(e) => { setDraft((d) => ({ ...d, from: e.target.value })); clearFieldError("from"); }}
              placeholder="6:04"
              style={withError(monoInputStyle, !!fieldErrors.from)}
            />
          </Field>
          <Field label="End or duration" error={fieldErrors.to}>
            <input
              value={draft.to ?? ""}
              onChange={(e) => { setDraft((d) => ({ ...d, to: e.target.value })); clearFieldError("to"); }}
              placeholder='7:18 or 1:30'
              style={withError(monoInputStyle, !!fieldErrors.to)}
            />
          </Field>
          <Field label="First spoken words">
            <input
              value={draft.startPhrase ?? ""}
              onChange={(e) => setDraft((d) => ({ ...d, startPhrase: e.target.value }))}
              placeholder='e.g. "Trauma is a disorder"'
              style={inputStyle}
            />
          </Field>
          <Field label="Last spoken words">
            <input
              value={draft.endPhrase ?? ""}
              onChange={(e) => setDraft((d) => ({ ...d, endPhrase: e.target.value }))}
              placeholder='e.g. "to themselves"'
              style={inputStyle}
            />
          </Field>
          <Field label="Host">
            <input
              value={draft.host ?? ""}
              onChange={(e) => setDraft((d) => ({ ...d, host: e.target.value }))}
              placeholder='e.g. Lewis Howes'
              style={inputStyle}
            />
          </Field>
          <Field label="Guests (comma-separated)">
            <input
              value={Array.isArray(draft.guests) ? draft.guests.join(", ") : (draft.guests ?? "")}
              onChange={(e) => setDraft((d) => ({ ...d, guests: e.target.value as unknown as string[] }))}
              placeholder='e.g. Mel Robbins'
              style={inputStyle}
            />
          </Field>
          <Field label="Topic">
            <input
              value={draft.topic ?? ""}
              onChange={(e) => setDraft((d) => ({ ...d, topic: e.target.value }))}
              placeholder='e.g. discipline beats motivation'
              style={inputStyle}
            />
          </Field>
        </div>
        <div style={{ marginTop: 12 }}>
          <Field label="Notes">
            <textarea
              value={draft.notes ?? ""}
              onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
              rows={2}
              placeholder="Why this clip — context, hook angle, anything to remember…"
              style={{ ...inputStyle, fontFamily: "inherit", resize: "vertical" }}
            />
          </Field>
        </div>
        <div style={{ marginTop: 16, display: "flex", justifyContent: isMobile ? "stretch" : "flex-end" }}>
          <button
            onClick={submitDraft}
            disabled={adding}
            style={{
              ...ctaSmallStyle,
              cursor: adding ? "wait" : "pointer",
              opacity: adding ? 0.6 : 1,
              ...(isMobile ? { width: "100%", justifyContent: "center" } : {}),
            }}
          >
            <Plus size={14} strokeWidth={2.5} /> {adding ? "Saving…" : "Add"}
          </button>
        </div>
      </div>

      <div style={{ fontSize: 13, color: "var(--ql-fg-muted)", fontFamily: "var(--ql-font-mono)", marginBottom: 12 }}>
        {inlineEntries.length} manual {inlineEntries.length === 1 ? "entry" : "entries"}
      </div>

      {scan && (
        <ScanProgressCard
          scan={scan}
          onDismiss={() => setScan(null)}
          onEdit={() => onEditScan?.(scan)}
          onOpenRender={
            scan.relatedJobId && onNavigateQueue
              ? () => onNavigateQueue(scan.relatedJobId!)
              : undefined
          }
        />
      )}

      {!loaded ? (
        <div style={emptyStyle}>Loading…</div>
      ) : inlineEntries.length === 0 ? (
        <div style={emptyStyle}>
          No manual entries yet. Add your first candidate above — or scan a video to fill the Scanned scenes below.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {inlineEntries.map((entry) => (
            <BacklogRow
              key={entry.id}
              entry={entry}
              onDelete={() => void handleDelete(entry.id)}
              onUseInNewJob={() =>
                onUseInNewJob({
                  label: entry.label,
                  url: entry.url,
                  from: entry.from,
                  to: entry.to,
                  duration: entry.duration,
                  startPhrase: entry.startPhrase,
                  endPhrase: entry.endPhrase,
                  host: entry.host,
                  guests: entry.guests,
                  topic: entry.topic,
                  notes: entry.notes,
                })
              }
            />
          ))}
        </div>
      )}

      {/* ── Scanned scenes — always visible inline, grouped by source video
          and sliced by the Fresh / Rendered / Archived chips (every scene
          lives under exactly one chip, so nothing can silently vanish).
          Header badge = the FRESH count (the working set); the chips carry
          their own counts + a "Scan history" jump to the Requests log. Each
          row renders/opens via the shared SceneRow. ── */}
      <div style={{ marginTop: 28, paddingTop: 22, borderTop: "1px solid rgba(255,255,255,.06)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", minWidth: 0 }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 15,
                fontWeight: 700,
                color: "var(--ql-fg)",
                padding: "2px 8px",
                borderRadius: 10,
                boxShadow: scenesJustGrew ? "0 0 0 4px rgba(255,213,0,.18)" : "none",
                transform: scenesJustGrew ? "translateY(-1px)" : "none",
                transition: "box-shadow .3s var(--ql-ease), transform .3s var(--ql-ease)",
              }}
            >
              <Film size={16} strokeWidth={2} color="var(--ql-yellow)" />
              Scanned scenes
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  fontFamily: "var(--ql-font-mono)",
                  padding: "1px 8px",
                  borderRadius: 999,
                  background: "rgba(255,213,0,.12)",
                  color: "var(--ql-yellow)",
                  border: "1px solid rgba(255,213,0,.30)",
                }}
                title={`${freshCount} fresh ${freshCount === 1 ? "scene" : "scenes"} waiting to render`}
              >
                {freshCount}
              </span>
            </div>
            {/* Filter chips — single-select, live counts. */}
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <SceneFilterChip label="Fresh" count={freshCount} active={sceneFilter === "fresh"} onClick={() => setSceneFilter("fresh")} />
              <SceneFilterChip label="Rendered" count={renderedCount} active={sceneFilter === "rendered"} onClick={() => setSceneFilter("rendered")} />
              <SceneFilterChip label="Archived" count={archivedCount} active={sceneFilter === "archived"} onClick={() => setSceneFilter("archived")} />
            </div>
          </div>
          {onNavigateRequests && (
            <button
              onClick={onNavigateRequests}
              title="Open the full scan/locate history"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                padding: "7px 13px",
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,.10)",
                background: "rgba(255,255,255,.03)",
                color: "var(--ql-fg-muted)",
                cursor: "pointer",
                fontSize: 12.5,
                fontWeight: 600,
                fontFamily: "inherit",
              }}
            >
              <History size={14} strokeWidth={2} /> Scan history
            </button>
          )}
        </div>

        {!loaded ? (
          <div style={emptyStyle}>Loading…</div>
        ) : sceneEntries.length === 0 ? (
          <div style={emptyStyle}>
            No scanned scenes yet. Paste a URL on New Job and run Auto-scan — candidates land here ready to render.
          </div>
        ) : visibleGroups.length === 0 ? (
          // Honest per-chip empty state — the scenes exist, just under the
          // other chips (their counts stay visible in the header).
          <div style={emptyStyle}>
            {sceneFilter === "fresh"
              ? "Nothing fresh — scan a video or check Rendered/Archived."
              : sceneFilter === "rendered"
              ? "Nothing rendered yet — render a fresh scene and it moves here."
              : "Nothing archived. Archiving shelves a scene here (never deletes it) so Fresh stays a working set."}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
            {visibleGroups.map((group) => {
              const freshInGroup = group.entries.filter((e) => sceneFilterOf(e) === "fresh");
              const archivedInGroup = group.entries.filter((e) => sceneFilterOf(e) === "archived");
              const bulkIds = sceneFilter === "fresh" ? freshInGroup.map((e) => e.id) : archivedInGroup.map((e) => e.id);
              return (
                <div key={group.key}>
                  <SceneGroupHeader
                    title={
                      group.entries.find((e) => e.sourceTitle)?.sourceTitle ??
                      group.entries[0]?.hookEn ??
                      group.entries[0]?.url ??
                      "(untitled source)"
                    }
                    thumbnailUrl={
                      group.entries[0]?.videoId
                        ? `https://img.youtube.com/vi/${group.entries[0].videoId}/hqdefault.jpg`
                        : undefined
                    }
                    counts={{
                      fresh: freshInGroup.length,
                      rendered: group.entries.filter((e) => sceneFilterOf(e) === "rendered").length,
                      archived: archivedInGroup.length,
                    }}
                    filter={sceneFilter}
                    isMobile={isMobile}
                    bulkCount={bulkIds.length}
                    confirmingBulk={confirmingArchiveGroupKey === group.key}
                    onRequestBulk={() => setConfirmingArchiveGroupKey(group.key)}
                    onCancelBulk={() => setConfirmingArchiveGroupKey(null)}
                    onConfirmBulk={() => void handleBulkArchive(bulkIds, sceneFilter === "fresh")}
                  />
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {group.visible.map((entry) => (
                      // Anchor + flash target for the locate-dedup "it's already
                      // here" path — settle() switches the filter chip if needed,
                      // then scrolls to `scene-<id>` and glows it briefly.
                      <div
                        key={entry.id}
                        id={`scene-${entry.id}`}
                        style={{
                          borderRadius: 14,
                          boxShadow: highlightEntryId === entry.id ? "0 0 0 3px rgba(96,165,250,.55)" : "none",
                          transition: "box-shadow .4s var(--ql-ease)",
                        }}
                      >
                        <SceneRow
                          entry={entry}
                          isMobile={isMobile}
                          channelsEnabled={channelsEnabled}
                          channels={channels}
                          confirming={confirmingDeleteId === entry.id}
                          musicTracks={musicTracks}
                          onRequestDelete={() => setConfirmingDeleteId(entry.id)}
                          onCancelDelete={() => setConfirmingDeleteId(null)}
                          onConfirmDelete={() => void handleSceneDelete(entry.id)}
                          onRendered={handleSceneRendered}
                          onEntryUpdated={handleSceneUpdated}
                          onNavigateQueue={onNavigateQueue}
                          onToggleArchive={() => void handleToggleArchive(entry)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Other-channel hint — SCAN-12 no-silent-vanish (CHANNELS_ENABLED only) */}
        {channelsEnabled && !!otherChannelScannedCount && otherChannelScannedCount > 0 && (
          <div
            style={{
              marginTop: 16,
              paddingTop: 14,
              borderTop: "1px solid rgba(255,255,255,.08)",
              fontSize: 11.5,
              color: "var(--ql-fg-muted)",
              lineHeight: 1.5,
            }}
          >
            {otherChannelScannedCount} more scanned{" "}
            {otherChannelScannedCount === 1 ? "scene" : "scenes"} in{" "}
            {otherChannelNames(channels, activeChannelSlug)} — switch channel to view.
          </div>
        )}
      </div>

      {/* Toast — fixed bottom-center confirmation that the render job was queued. */}
      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 60,
            display: "inline-flex",
            alignItems: "center",
            gap: 12,
            padding: "10px 18px",
            borderRadius: 12,
            background: "rgba(16,18,27,.98)",
            border: "1px solid rgba(74,222,128,.30)",
            color: "var(--ql-fg)",
            fontSize: 13,
            fontWeight: 600,
            boxShadow: "0 8px 32px rgba(0,0,0,.45)",
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
            <CheckCircle2 size={15} strokeWidth={2} color="var(--ql-success)" />
            Sent to the render queue.
          </span>
          {onNavigateQueue && (
            <button
              onClick={() => onNavigateQueue(toast.jobId)}
              style={{
                background: "none",
                border: "none",
                color: "var(--ql-yellow)",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 700,
                fontFamily: "inherit",
                textDecoration: "underline",
                padding: 0,
              }}
            >
              Go to Queue
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** One Fresh/Rendered/Archived filter chip — pill button + mono count,
 *  yellow-accented when active (mirrors the section header's count pill). */
function SceneFilterChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      title={`Show ${label.toLowerCase()} scenes (${count})`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 12px",
        borderRadius: 999,
        border: active ? "1px solid rgba(255,213,0,.40)" : "1px solid rgba(255,255,255,.10)",
        background: active ? "rgba(255,213,0,.12)" : "rgba(255,255,255,.03)",
        color: active ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
        cursor: "pointer",
        fontSize: 12,
        fontWeight: 600,
        fontFamily: "inherit",
        transition: "all .15s var(--ql-ease)",
      }}
    >
      {label}
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          fontFamily: "var(--ql-font-mono)",
          padding: "0 6px",
          borderRadius: 999,
          background: active ? "rgba(255,213,0,.14)" : "rgba(255,255,255,.06)",
          border: active ? "1px solid rgba(255,213,0,.30)" : "1px solid rgba(255,255,255,.08)",
        }}
      >
        {count}
      </span>
    </button>
  );
}

/** Per-video group header: small source thumbnail + title + non-zero state
 *  counts, and the filter-appropriate bulk action — "Archive remaining" (with
 *  inline Yes/Cancel confirm, mirroring the delete confirm) on Fresh,
 *  "Restore all" on Archived, nothing on Rendered. */
function SceneGroupHeader({
  title,
  thumbnailUrl,
  counts,
  filter,
  isMobile,
  bulkCount,
  confirmingBulk,
  onRequestBulk,
  onCancelBulk,
  onConfirmBulk,
}: {
  title: string;
  thumbnailUrl?: string;
  counts: { fresh: number; rendered: number; archived: number };
  filter: SceneFilter;
  isMobile: boolean;
  /** Rows the bulk action would touch (fresh rows to archive / archived rows
   *  to restore). 0 hides the button. */
  bulkCount: number;
  confirmingBulk: boolean;
  onRequestBulk: () => void;
  onCancelBulk: () => void;
  onConfirmBulk: () => void;
}): React.JSX.Element {
  const countParts = [
    counts.fresh > 0 ? `${counts.fresh} fresh` : null,
    counts.rendered > 0 ? `${counts.rendered} rendered` : null,
    counts.archived > 0 ? `${counts.archived} archived` : null,
  ].filter((p): p is string => p !== null);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "stretch" : "center",
        gap: isMobile ? 8 : 12,
        marginBottom: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
        {thumbnailUrl && (
          <img
            src={thumbnailUrl}
            alt=""
            style={{
              width: 56,
              height: 32,
              objectFit: "cover",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,.08)",
              flexShrink: 0,
              display: "block",
            }}
          />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            title={title}
            style={{
              fontSize: 12.5,
              fontWeight: 700,
              color: "var(--ql-fg)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </div>
          <div style={{ fontSize: 10.5, color: "var(--ql-fg-subtle)", fontFamily: "var(--ql-font-mono)", marginTop: 2 }}>
            {countParts.join(" · ")}
          </div>
        </div>
      </div>
      {filter === "fresh" && bulkCount > 0 && (
        confirmingBulk ? (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              ...(isMobile ? { justifyContent: "flex-end" } : {}),
            }}
          >
            <span style={{ fontSize: 11, color: "var(--ql-fg-muted)" }}>
              Archive {bulkCount} scene{bulkCount === 1 ? "" : "s"}?
            </span>
            <button onClick={onConfirmBulk} style={bulkConfirmYesStyle}>
              Yes
            </button>
            <button onClick={onCancelBulk} style={bulkConfirmCancelStyle}>
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={onRequestBulk}
            title="Archive every fresh scene from this video — nothing is deleted, find them under Archived"
            style={{ ...groupBulkBtnStyle, ...(isMobile ? { width: "100%", justifyContent: "center" } : {}) }}
          >
            <Archive size={12} strokeWidth={2} /> Archive remaining
          </button>
        )
      )}
      {filter === "archived" && bulkCount > 0 && (
        <button
          onClick={onConfirmBulk}
          title="Restore every archived scene from this video back to Fresh"
          style={{ ...groupBulkBtnStyle, ...(isMobile ? { width: "100%", justifyContent: "center" } : {}) }}
        >
          <ArchiveRestore size={12} strokeWidth={2} /> Restore all
        </button>
      )}
    </div>
  );
}

const groupBulkBtnStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 12px",
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,.10)",
  background: "rgba(255,255,255,.03)",
  color: "var(--ql-fg-muted)",
  cursor: "pointer",
  fontSize: 11.5,
  fontWeight: 600,
  fontFamily: "inherit",
  flexShrink: 0,
};

// Inline Yes/Cancel — same shape as SceneRow's delete confirm, but yellow on
// Yes: archiving is reversible (never destructive), so it doesn't wear red.
const bulkConfirmYesStyle: CSSProperties = {
  padding: "5px 10px",
  borderRadius: 8,
  border: "1px solid rgba(255,213,0,.40)",
  background: "rgba(255,213,0,.14)",
  color: "var(--ql-yellow)",
  cursor: "pointer",
  fontSize: 11,
  fontWeight: 700,
  fontFamily: "inherit",
};

const bulkConfirmCancelStyle: CSSProperties = {
  padding: "5px 10px",
  borderRadius: 8,
  border: "1px solid rgba(255,255,255,.12)",
  background: "rgba(255,255,255,.03)",
  color: "var(--ql-fg-muted)",
  cursor: "pointer",
  fontSize: 11,
  fontWeight: 600,
  fontFamily: "inherit",
};

/** Comma-joined display names of every channel except the active one — used in
 *  the cross-channel "more scenes elsewhere" hint (CHANNELS_ENABLED only). */
function otherChannelNames(
  channels?: { slug: string; displayName: string }[],
  activeChannelSlug?: string,
): string {
  if (!channels || channels.length === 0) return "another channel";
  const names = channels
    .filter((c) => c.slug !== activeChannelSlug)
    .map((c) => c.displayName)
    .filter(Boolean);
  return names.length > 0 ? names.join(", ") : "another channel";
}

function Field({
  label,
  children,
  error,
}: {
  label: string;
  children: React.ReactNode;
  error?: string;
}): React.JSX.Element {
  return (
    <div>
      <div style={fieldLabelStyle}>{label}</div>
      {children}
      {error && (
        <div
          style={{
            marginTop: 6,
            fontSize: 11,
            color: "var(--ql-danger)",
            fontFamily: "var(--ql-font-mono)",
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

/** Tint an input red when its field has a validation error. */
function withError(base: CSSProperties, hasError: boolean): CSSProperties {
  if (!hasError) return base;
  return {
    ...base,
    borderColor: "var(--ql-danger)",
    boxShadow: "0 0 0 1px rgba(248,113,113,.30) inset",
  };
}

function ScanProgressCard({
  scan,
  onDismiss,
  onEdit,
  onOpenRender,
}: {
  scan: ScanResult;
  onDismiss: () => void;
  onEdit?: () => void;
  /** Present when the result deduped against an ALREADY-RENDERED scene —
   *  routes to that render's job page instead of dead-ending on "0 found". */
  onOpenRender?: () => void;
}): React.JSX.Element {
  const failed = scan.status === "failed";
  const done = scan.status === "done";
  const found = scan.candidateCount ?? 0;
  // The scene the operator asked for already exists on the shelf (locate dedup).
  const isDup = done && found === 0 && !!(scan.relatedEntryId || scan.relatedJobId);
  const pct = Math.round(Math.min(1, Math.max(0, scan.progress ?? 0)) * 100);
  // A targeted "find this scene" locate carries the opening/closing lines; a
  // broad auto-scan does not. Drives locate-specific in-progress wording.
  const isLocate = !!(scan.openingLine || scan.closingLine);

  // The success / empty card auto-clears after a beat so it doesn't linger; a
  // failed card stays put so the operator can read the error and retry. The
  // dup card lingers longer — it carries an action (Open render / highlight).
  useEffect(() => {
    if (!done) return;
    const t = window.setTimeout(onDismiss, isDup ? 20000 : 9000);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  const accent = failed
    ? "var(--ql-danger)"
    : done && found > 0
    ? "var(--ql-success)"
    : done
    ? "rgba(255,255,255,.18)"
    : "rgba(255,213,0,.30)";
  const tint = failed
    ? "rgba(248,113,113,.06)"
    : done && found > 0
    ? "rgba(74,222,128,.06)"
    : done
    ? "rgba(255,255,255,.03)"
    : "rgba(255,213,0,.05)";

  return (
    <div style={{ ...cardStyle, marginBottom: 12, padding: "16px 18px", border: `1px solid ${accent}`, background: tint }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {failed ? (
          <span style={{ color: "var(--ql-danger)", fontSize: 14, fontWeight: 700 }}>Scan failed</span>
        ) : done ? (
          <>
            <CheckCircle2
              size={16}
              strokeWidth={2.2}
              color={found > 0 ? "var(--ql-success)" : isDup ? "#60a5fa" : "var(--ql-fg-muted)"}
              style={{ flexShrink: 0 }}
            />
            <span style={{ fontSize: 14, fontWeight: 700, color: "var(--ql-fg)" }}>
              {found > 0
                ? `Found ${found} scene${found === 1 ? "" : "s"}`
                : isDup
                ? "This scene is already saved"
                : "Scan complete — no scenes this time"}
            </span>
          </>
        ) : (
          <>
            <span
              style={{
                display: "inline-block",
                width: 15,
                height: 15,
                border: "2px solid var(--ql-yellow)",
                borderTopColor: "transparent",
                borderRadius: "50%",
                animation: "ql-spin 0.8s linear infinite",
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: 14, fontWeight: 700, color: "var(--ql-fg)" }}>
              {isLocate ? "Locating your exact scene…" : "Scanning your video for the best moments…"}
            </span>
          </>
        )}

        <div style={{ marginLeft: "auto", display: "inline-flex", gap: 8, alignItems: "center" }}>
          {isDup && onOpenRender && (
            <button
              onClick={onOpenRender}
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "5px 12px", borderRadius: 999,
                background: "rgba(96,165,250,.14)", border: "1px solid rgba(96,165,250,.40)",
                color: "#60a5fa", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit",
              }}
            >
              Open render
            </button>
          )}
          {failed && onEdit && scan.openingLine && (
            <button
              onClick={onEdit}
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "5px 12px", borderRadius: 999,
                background: "rgba(96,165,250,.14)", border: "1px solid rgba(96,165,250,.40)",
                color: "#60a5fa", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit",
              }}
            >
              <Crosshair size={12} strokeWidth={2.5} /> Edit &amp; retry
            </button>
          )}
          {failed || done ? (
            <button
              onClick={onDismiss}
              style={{ background: "none", border: "none", color: "var(--ql-fg-muted)", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}
            >
              Dismiss
            </button>
          ) : (
            <span style={{ fontSize: 11, fontFamily: "var(--ql-font-mono)", color: "var(--ql-fg-muted)" }}>{pct}%</span>
          )}
        </div>
      </div>

      <div style={{ fontSize: 12, color: failed ? "var(--ql-danger)" : "var(--ql-fg-muted)", marginTop: 6 }}>
        {failed
          ? scan.errorMsg || "Something went wrong during the scan."
          : done && found > 0
          ? "Saved below in Scanned scenes — hit Render on any one to ship it."
          : isDup
          ? `${scan.detail || "This scene is already in your Backlog."}${scan.relatedEntryId ? " It's highlighted below." : ""}`
          : done
          ? scan.detail || "Nothing cleared the quality bar here. Try another video or a different stretch."
          : scan.detail || "Starting…"}
      </div>

      {!failed && !done && (
        <>
          <div style={{ marginTop: 10, height: 4, borderRadius: 999, background: "rgba(255,255,255,.08)", overflow: "hidden" }}>
            <div style={{ width: `${Math.max(6, pct)}%`, height: "100%", background: "var(--ql-yellow)", transition: "width .3s var(--ql-ease)" }} />
          </div>
          <div style={{ fontSize: 11, color: "var(--ql-fg-subtle)", marginTop: 8 }}>
            Short videos finish in a couple of minutes; a multi-hour podcast can take 10+. Finished scenes appear below in{" "}
            <strong style={{ color: "var(--ql-fg-muted)", fontWeight: 700 }}>Scanned scenes</strong> — you can keep working, this card will stay until it's done.
          </div>
        </>
      )}
    </div>
  );
}

function BacklogRow({
  entry,
  onDelete,
  onUseInNewJob,
}: {
  entry: BacklogEntry;
  onDelete: () => void;
  onUseInNewJob: () => void;
}): React.JSX.Element {
  const isMobile = useIsMobile();
  return (
    <div
      style={{
        ...cardStyle,
        padding: isMobile ? 14 : 16,
      }}
    >
      <div style={{ display: "flex", alignItems: isMobile ? "flex-start" : "baseline", flexDirection: isMobile ? "column" : "row", gap: 12, marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0, width: isMobile ? "100%" : undefined }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ql-fg)" }}>
            {entry.label || entry.url || "(untitled)"}
          </div>
          <div style={{ fontSize: 11, color: "var(--ql-fg-subtle)", marginTop: 2 }}>
            saved {new Date(entry.createdAt).toLocaleString()}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", width: isMobile ? "100%" : undefined }}>
          <button
            onClick={onUseInNewJob}
            title="Pre-fill the New Job form with these values"
            style={{ ...ctaSmallStyle, ...(isMobile ? { flex: 1, justifyContent: "center" } : {}) }}
          >
            <Send size={13} strokeWidth={2} /> Use in New Job
          </button>
          <button
            onClick={onDelete}
            title="Delete from backlog"
            style={iconBtnStyle}
          >
            <Trash2 size={14} strokeWidth={1.85} />
          </button>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {entry.url && <CopyField label="URL" value={entry.url} mono />}
        {entry.from && <CopyField label="Start" value={entry.from} mono />}
        {entry.to && <CopyField label="End / duration" value={entry.to} mono />}
        {entry.startPhrase && <CopyField label="First words" value={entry.startPhrase} />}
        {entry.endPhrase && <CopyField label="Last words" value={entry.endPhrase} />}
        {entry.host && <CopyField label="Host" value={entry.host} />}
        {entry.guests && entry.guests.length > 0 && <CopyField label="Guests" value={entry.guests.join(", ")} />}
        {entry.topic && <CopyField label="Topic" value={entry.topic} />}
      </div>
      {entry.notes && (
        <div
          style={{
            marginTop: 10,
            padding: "8px 12px",
            borderRadius: 10,
            background: "rgba(255,213,0,.04)",
            border: "1px solid rgba(255,213,0,.12)",
            fontSize: 12,
            color: "var(--ql-fg-muted)",
            lineHeight: 1.5,
            whiteSpace: "pre-wrap",
          }}
        >
          {entry.notes}
        </div>
      )}
    </div>
  );
}

function CopyField({ label, value, mono }: { label: string; value: string; mono?: boolean }): React.JSX.Element {
  const isMobile = useIsMobile();
  const [copied, setCopied] = useState(false);
  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard blocked — silent fail */
    }
  };
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "80px 1fr 28px" : "100px 1fr 28px",
        alignItems: "center",
        gap: 10,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: "var(--ql-fg-subtle)",
          textTransform: "uppercase",
          letterSpacing: ".06em",
        }}
      >
        {label}
      </div>
      <div
        title={value}
        style={{
          fontSize: 12,
          color: "var(--ql-gray-200)",
          fontFamily: mono ? "var(--ql-font-mono)" : "inherit",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </div>
      <button
        type="button"
        onClick={handleCopy}
        title={copied ? "Copied" : "Copy"}
        style={{
          width: 26,
          height: 26,
          borderRadius: 8,
          border: "1px solid rgba(255,255,255,.08)",
          background: copied ? "rgba(74,222,128,.12)" : "rgba(255,255,255,.02)",
          color: copied ? "var(--ql-success)" : "var(--ql-fg-muted)",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all .15s var(--ql-ease)",
        }}
      >
        {copied ? <Check size={13} strokeWidth={2.5} /> : <Copy size={13} strokeWidth={1.85} />}
      </button>
    </div>
  );
}

const iconBtnStyle: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,.08)",
  background: "rgba(255,255,255,.02)",
  color: "var(--ql-fg-muted)",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "inherit",
};

const emptyStyle: React.CSSProperties = {
  background: "rgba(16,18,27,.72)",
  border: "1px dashed rgba(255,255,255,.10)",
  borderRadius: 18,
  padding: 60,
  textAlign: "center",
  color: "var(--ql-fg-muted)",
  fontSize: 14,
};
